import type { BlockId } from '@/types/block';
import type { IWeekDay } from '@/types/calendar';
import type { IBlockDayOffset, IBlockSettings, ICalendarPosition } from '@/types/schedule';

import { CachedBlock } from '@/classes/cachedBlock';
import { BLOCKS_IN_HOUR, HOUR_IN_PX } from '@/config/variables';
import { convertStartBlockToDayOffset, formatDate, formatWithMinutes } from '@/helpers';

export const getDateFromBlock = (blockId: BlockId, timezoneName: string): Date => {
  const block = CachedBlock.fromBlockId(blockId, timezoneName);
  return new Date(
    Number(block.year),
    Number(block.month - 1),
    Number(block.day),
    Number(block.hours24),
    Number(block.minutes),
    0,
  );
};

/**
 * Calculates basic data for given block - startsAt, endsAt, tz and duration
 */
export const getBlockSettings = (
  startBlockId: BlockId,
  endBlockId: BlockId,
  timeZone: string,
  day?: IWeekDay,
): IBlockSettings => {
  const startBlock = CachedBlock.fromBlockId(startBlockId, timeZone);
  const endBlock = CachedBlock.fromBlockId(endBlockId + 1, timeZone);

  const startAt = getDateFromBlock(startBlock.blockId, timeZone);
  const endAt = getDateFromBlock(endBlock.blockId, timeZone);
  const diff = (endBlock.blockId - startBlock.blockId) / BLOCKS_IN_HOUR;
  const blockDuration = diff * HOUR_IN_PX;

  let startTzAbbreviation = day?.tzAbbreviation ?? '';
  let endTzAbbreviation = day?.tzAbbreviation ?? '';
  if (!day || day.isDlsDay) {
    startTzAbbreviation = startBlock.getTimezoneAbbreviation();
    endTzAbbreviation = endBlock.getTimezoneAbbreviation();
  }

  return {
    startAt,
    endAt,
    startTz: startTzAbbreviation,
    endTz: endTzAbbreviation,
    blockDuration,
    timezoneName: timeZone,
  };
};

/**
 * Calculates offset from start of day for given block
 */
export const getBlockDayOffset = (startBlock: BlockId, timeZone: string, day?: IWeekDay): IBlockDayOffset =>
  convertStartBlockToDayOffset(startBlock, timeZone, day);

/**
 * Combines data from getBlockSettings and getBlockDayOffset to return necessary info to position shift
 */
export const getCalendarPosition = (
  startBlock: BlockId,
  endBlock: BlockId,
  timeZone: string,
  day?: IWeekDay,
): ICalendarPosition => {
  const blockSettings = getBlockSettings(startBlock, endBlock, timeZone, day);
  const minutesFromMidnight = getBlockDayOffset(startBlock, timeZone, day);
  return {
    ...blockSettings,
    minutesFromMidnight,
  };
};

/**
 * Formats given block range using the provided date format. By default, it uses {@link formatWithMinutes}
 */
export const formatStartEndTimeWithTimezone = (
  blockSettings: IBlockSettings,
  formats?: { startFormat: string; endFormat: string },
): string => {
  const start = formatDate(blockSettings.startAt, formats?.startFormat || formatWithMinutes(blockSettings.startAt));
  const end = formatDate(blockSettings.endAt, formats?.endFormat || formatWithMinutes(blockSettings.endAt));

  const { startTz, endTz } = blockSettings;
  if (startTz !== endTz) return `${start}(${startTz}) - ${end}(${endTz})`;

  return `${start} - ${end}`;
};
